import React, { useState } from 'react';
import { Mail, Phone, Clock, Send } from 'lucide-react';

const REGIONS = [
  {
    flag: '/assets/img/panda/flags/canada.svg',
    country: 'Canada',
    city: 'Toronto, ON',
    email: 'ca-support@pandaonwheels.com',
    phone: '+1 (800) 555-0199',
  },
  {
    flag: '/assets/img/panda/flags/us.svg',
    country: 'United States',
    city: 'Reno, NV',
    email: 'us-support@pandaonwheels.com',
    phone: '+1 (800) 555-0142',
  },
  {
    flag: '/assets/img/panda/flags/uk.svg',
    country: 'United Kingdom',
    city: 'London',
    email: 'uk-support@pandaonwheels.com',
    phone: '+44 20 7946 0958',
  },
];

const Contact = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });

  const setField = (field, value) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = e => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `Message from ${form.name || 'website'}${
        form.company ? ` (${form.company})` : ''
      }`,
    );
    const body = encodeURIComponent(
      `${form.message}\n\n—\n${form.name}\n${form.email}${
        form.company ? `\n${form.company}` : ''
      }`,
    );
    window.location.href = `mailto:support@pandaonwheels.com?subject=${subject}&body=${body}`;
  };

  return (
    <main className="bg-[#FDF6EE] pt-[88px]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-6">
        <p className="text-[11px] sm:text-xs font-bold text-[#E53935] uppercase tracking-wider mb-2">
          Get In Touch
        </p>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#202124] leading-tight">
          Contact Us
        </h1>
        <p className="mt-4 text-gray-500 text-sm sm:text-base max-w-xl">
          Questions about an order, a catered event, or getting your team set
          up? We&apos;re happy to help.
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <a
            href="mailto:support@pandaonwheels.com"
            className="bg-white rounded-2xl border border-gray-200/80 p-5 flex items-center gap-4 hover:border-[#E53935]/40 hover:shadow-md transition-all"
          >
            <span className="w-12 h-12 rounded-full bg-panda-tint flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-[#E53935]" />
            </span>
            <span>
              <span className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                Email us
              </span>
              <span className="block font-heading font-bold text-sm text-[#202124] mt-0.5">
                support@pandaonwheels.com
              </span>
            </span>
          </a>

          <a
            href="tel:+18005550199"
            className="bg-white rounded-2xl border border-gray-200/80 p-5 flex items-center gap-4 hover:border-[#E53935]/40 hover:shadow-md transition-all"
          >
            <span className="w-12 h-12 rounded-full bg-panda-tint flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-[#E53935]" />
            </span>
            <span>
              <span className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                Call us
              </span>
              <span className="block font-heading font-bold text-sm text-[#202124] mt-0.5">
                +1 (800) 555-0199
              </span>
            </span>
          </a>

          <div className="bg-white rounded-2xl border border-gray-200/80 p-5 flex items-center gap-4">
            <span className="w-12 h-12 rounded-full bg-panda-tint flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-[#E53935]" />
            </span>
            <span>
              <span className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                Support hours
              </span>
              <span className="block font-heading font-bold text-sm text-[#202124] mt-0.5">
                Mon–Fri, 9am–6pm
              </span>
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <h2 className="font-heading font-bold text-lg sm:text-xl text-[#202124] mb-1">
              Our offices
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              We deliver across Canada, the US and the UK — reach the team
              closest to you.
            </p>
            <div className="space-y-3">
              {REGIONS.map(region => (
                <div
                  key={region.country}
                  className="bg-white rounded-2xl border border-gray-200/80 p-4 flex items-center gap-4"
                >
                  <img
                    src={region.flag}
                    alt={region.country}
                    className="w-12 h-8 rounded-md object-cover flex-shrink-0 border border-gray-100"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-sm text-[#202124]">
                      {region.country}
                    </p>
                    <p className="text-xs text-gray-400">{region.city}</p>
                  </div>
                  <div className="text-right flex-shrink-0 hidden sm:block">
                    <a
                      href={`mailto:${region.email}`}
                      className="block text-xs text-[#E53935] font-semibold hover:underline"
                    >
                      {region.email}
                    </a>
                    <a
                      href={`tel:${region.phone.replace(/[^+\d]/g, '')}`}
                      className="block text-xs text-gray-500 mt-0.5 hover:text-[#E53935]"
                    >
                      {region.phone}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl border border-gray-200/80 p-6 sm:p-8">
              <h2 className="font-heading font-bold text-lg sm:text-xl text-[#202124] mb-1">
                Send us a message
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Fill this in and it&apos;ll open in your email app, ready to
                send.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1"
                    >
                      Name
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={e => setField('name', e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-email"
                      className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1"
                    >
                      Email
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={e => setField('email', e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="contact-company"
                    className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1"
                  >
                    Company (optional)
                  </label>
                  <input
                    id="contact-company"
                    type="text"
                    value={form.company}
                    onChange={e => setField('company', e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-message"
                    className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1"
                  >
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    rows={5}
                    required
                    value={form.message}
                    onChange={e => setField('message', e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-gray-50/50 rounded-xl border border-gray-200 text-sm text-[#202124] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935] resize-none"
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E53935] text-white text-sm font-heading font-bold hover:bg-[#d32f2f] transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Contact;
